import { isAfter } from "date-fns";
import { Table, OptionsDataType, ColDataType, RowDataType } from "@thrivetribe/mvf.ui.organisms.table";
import { Arrow } from "@thrivetribe/mvf.assets.icons.arrow";
import { ScoreBreakdown, ScoreBreakdownStatsProp } from "ui/molecules/ScoreBreakdown";
import { ClubStats as ClubStatsProp } from "types/Legacy/ClubStats";
import "../empty-table.scss";
import "./LatestResults.scss";

const ExpandCtrlEl = (): JSX.Element => {
  return (
    <>
      <Arrow />
      <span>Score breakdown</span>
    </>
  );
};

const columns: ColDataType[] = [
  { id: "leftCol", label: "" },
  { id: "middleCol", label: "", align: "center" },
  { id: "rightCol", label: "" },
];

interface CustomRowDataType extends RowDataType {
  leftCol?: string;
  middleCol?: JSX.Element;
  rightCol?: string;
}

interface LatestResultsProps {
  title: string;
  stats: ClubStatsProp["ListLeagueFixtures"] | ClubStatsProp["ListLeagueCupFixtures"] | undefined;
}
export const LatestResults = ({ title, stats }: LatestResultsProps): JSX.Element => {
  const options: OptionsDataType = {
    tableAriaLabel: title,
    tablePrefixClass: "latest-results",
    headerSticky: true,
    headerEl: <h2>{title}</h2>,
    headerColTitlesDisabled: true,
  };

  const fixtures = stats ?? [];
  const noResultFixtures: boolean[] = [];

  const rows: CustomRowDataType[][] = fixtures.map((fixture) => {
    const fixtureDateTime = fixture.FixtureDateTime?.replaceAll(/Date|\/|\(|\)/g, "");
    const fixtureTimeMilliseconds = Number(fixtureDateTime);

    if (
      (fixtureDateTime !== undefined && isAfter(fixtureTimeMilliseconds, new Date())) ||
      (fixture.ListOfFixtures[0].AwayTeamScore === null && fixture.ListOfFixtures[0].HomeTeamScore === null)
    ) {
      noResultFixtures.push(true);
      return [{}];
    } else {
      noResultFixtures.push(false);
    }

    const fixtureHeaderRow: CustomRowDataType = {
      middleCol: <time dateTime={fixture.FixtureDate_Str}>{fixture.FixtureDate_Str}</time>,
      options: {
        className: "table__body__row--subtitle",
        colSpan: 3,
      },
    };

    const fixtureBodyRows: CustomRowDataType[] = fixture.ListOfFixtures.map((fixture) => {
      const homeTeamStats: ScoreBreakdownStatsProp = {
        score: fixture.HomeTeamScore ?? 0,
        hatricks: fixture.HomeTeam_Hatricks,
        ownGoals: fixture.HomeTeam_OwnGoals,
        percentageBonuses: fixture.HomeTeam_XPercBonuses,
        teamBonuses: fixture.HomeTeam_TeamBonuses,
        trackingGoals: fixture.HomeTeam_TrackingGoals_Bonus,
        misc: fixture.HomeTeam_CommissionerBonus ?? 0,
      };

      const awayTeamStats: ScoreBreakdownStatsProp = {
        score: fixture.AwayTeamScore ?? 0,
        hatricks: fixture.AwayTeam_Hatricks,
        ownGoals: fixture.AwayTeam_OwnGoals,
        percentageBonuses: fixture.AwayTeam_XPercBonuses,
        teamBonuses: fixture.AwayTeam_TeamBonuses,
        trackingGoals: fixture.AwayTeam_TrackingGoals_Bonus,
        misc: fixture.AwayTeam_CommissionerBonus ?? 0,
      };

      return {
        leftCol: fixture.HomeTeamName,
        middleCol: (
          <span className="score">
            {fixture.HomeTeamTotal ?? 0} - {fixture.AwayTeamTotal ?? 0}
          </span>
        ),
        rightCol: fixture.AwayTeamName,
        options: {
          hoverDisabled: true,
          expandableEl: <ScoreBreakdown homeTeam={homeTeamStats} awayTeam={awayTeamStats} />,
          expandCtrlEl: <ExpandCtrlEl />,
          expandCtrlColIndex: 1,
        },
      };
    });

    return [fixtureHeaderRow, ...fixtureBodyRows];
  });

  if (fixtures.length === 0 || !noResultFixtures.includes(false)) {
    return (
      <article className="latest-results-container">
        <section className="empty-table">
          <div className="empty-table__heading">
            <h2>{title}</h2>
          </div>
          <div className="empty-table__content">
            <span>Looks like there are no results to show at the moment</span>
          </div>
        </section>
      </article>
    );
  }

  return (
    <article className="latest-results-container">
      <Table<CustomRowDataType> options={options} columns={columns} rows={rows} />
    </article>
  );
};
